import React, { useRef, useState } from "react";
import axios from "axios";
import Bugsnag from "@bugsnag/js";
import constants from "../../Constants";

const SEGMENTS = [3, 4, 5, 6, 7, 8, 9, 10];
const SPIN_MS_MIN = 3000;
const SPIN_MS_MAX = 5000;

function pickSpinMs() {
  return SPIN_MS_MIN + Math.round(Math.random() * (SPIN_MS_MAX - SPIN_MS_MIN));
}

const PromoRaffle = ({
  collection,
  delivery,
  bodylabel,
  truckType = "",
  promocode = "",
  onApply,
}) => {
  const [phase, setPhase] = useState("idle");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [rotation, setRotation] = useState(0);
  const [spinMs, setSpinMs] = useState(4000);
  const spinLock = useRef(false);

  const canSpin =
    !!(collection?.lat && delivery?.lat) &&
    !(collection.lat === delivery.lat && collection.lng === delivery.lng);

  const applied =
    !!(result?.promocode && promocode === result.promocode);

  const landOn = (percent, fromDeg, durationMs) => {
    const idx = Math.max(0, SEGMENTS.indexOf(percent));
    const slice = 360 / SEGMENTS.length;
    const target = 360 - (idx * slice + slice / 2);
    const extraTurns = (durationMs >= 4000 ? 6 : 5) * 360;
    return fromDeg + extraTurns + ((target - (fromDeg % 360) + 360) % 360);
  };

  const spin = () => {
    if (spinLock.current || !canSpin) return;
    spinLock.current = true;
    setError("");
    setPhase("spinning");

    const previousCode =
      result?.promocode ||
      (/^SPIN/i.test(promocode || "") ? promocode : "");
    if (previousCode && promocode === previousCode) {
      onApply?.("");
    }

    const started = Date.now();
    const duration = pickSpinMs();
    setSpinMs(duration);

    axios
      .post(constants.baseURL + "/promo-raffle", {
        collectionlat: collection.lat,
        collectionlng: collection.lng,
        deliverylat: delivery.lat,
        deliverylng: delivery.lng,
        bodylabel: bodylabel || "",
        truckType: truckType || "",
        previousCode,
      })
      .then((res) => {
        const percent = Number(res.data?.discountPercent) || 3;
        const remaining = Math.max(0, duration - (Date.now() - started));
        setSpinMs(remaining);
        setRotation(landOn(percent, rotation, duration));
        window.setTimeout(() => {
          setResult(res.data);
          setPhase("won");
          spinLock.current = false;
        }, remaining);
      })
      .catch((e) => {
        Bugsnag.notify(e);
        setPhase(result ? "won" : "idle");
        setError("Could not spin right now. Try again.");
        spinLock.current = false;
      });
  };

  const won = phase === "won" && result;

  return (
    <div
      className={
        "lp2-raffle" +
        (won ? " is-won" : "") +
        (phase === "spinning" ? " is-spinning" : "")
      }
    >
      {won && (
        <span className="lp2-raffle-confetti" aria-hidden="true">
          {Array.from({ length: 12 }, (_, i) => (
            <i key={`${result.promocode}-${i}`} />
          ))}
        </span>
      )}
      <div className="lp2-raffle-copy">
        <span className="lp2-raffle-route" aria-hidden="true">
          <i className="mdi mdi-map-marker" />
          Pickup
          <span className="lp2-raffle-route-line" />
          <i className="mdi mdi-truck-fast-outline" />
          <span className="lp2-raffle-route-line" />
          Drop-off
          <i className="mdi mdi-map-marker-check" />
        </span>
        {won ? (
          <>
            <span className="lp2-raffle-kicker">Discount loaded</span>
            <span className="lp2-raffle-percent" aria-live="polite">
              {result.discountPercent}% off this trip
            </span>
            <span className="lp2-raffle-code" title="Promo code">
              {result.promocode}
            </span>
            <span className="lp2-raffle-sub">
              {applied
                ? "On this trip. Pick a vehicle below to keep it."
                : "Add it to this trip, then pick your vehicle."}
            </span>
          </>
        ) : (
          <>
            <span className="lp2-raffle-title">
              {phase === "spinning"
                ? "Finding your trip discount"
                : "Trip discount for this route"}
            </span>
            <span className="lp2-raffle-sub">
              Spin the dial for 3% to 10% off. Shorter trips can land higher.
              Long hauls stay closer to 3%.
            </span>
          </>
        )}
        {error && (
          <span className="lp2-raffle-error" role="alert">
            {error}
          </span>
        )}
        <div className="lp2-raffle-actions">
          {won && (
            <button
              type="button"
              className={
                "lp2-raffle-btn" + (applied ? " is-applied" : "")
              }
              onClick={() => onApply?.(result.promocode)}
              disabled={applied}
            >
              {applied ? (
                <>
                  <i className="mdi mdi-check" aria-hidden="true" />
                  On this trip
                </>
              ) : (
                <>
                  <i className="mdi mdi-clipboard-check-outline" aria-hidden="true" />
                  Add to trip
                </>
              )}
            </button>
          )}
          <button
            type="button"
            className={
              "lp2-raffle-btn" + (won ? " lp2-raffle-btn--ghost" : "")
            }
            onClick={spin}
            disabled={!canSpin || phase === "spinning"}
          >
            <i
              className={
                "mdi " +
                (phase === "spinning"
                  ? "mdi-loading mdi-spin"
                  : "mdi-compass-outline")
              }
              aria-hidden="true"
            />
            {phase === "spinning"
              ? "Dispatching..."
              : won
                ? "Spin again"
                : "Spin for discount"}
          </button>
        </div>
      </div>
      <button
        type="button"
        className="lp2-raffle-wheel-wrap"
        onClick={spin}
        disabled={!canSpin || phase === "spinning"}
        aria-label={
          phase === "spinning"
            ? "Spinning"
            : won
              ? "Spin again"
              : "Spin for discount"
        }
      >
        <span className="lp2-raffle-pointer" />
        <div
          className="lp2-raffle-wheel"
          style={{
            transform: `rotate(${rotation}deg)`,
            transition:
              phase === "spinning"
                ? `transform ${spinMs}ms cubic-bezier(0.08, 0.78, 0.14, 1)`
                : "none",
          }}
        >
          {SEGMENTS.map((n, i) => (
            <span
              key={n}
              className="lp2-raffle-seg"
              style={{
                transform: `rotate(${i * 45 + 22.5}deg) translateY(-42px) rotate(${-(i * 45 + 22.5)}deg)`,
              }}
            >
              {n}%
            </span>
          ))}
        </div>
        <span className="lp2-raffle-hub">
          <i className="mdi mdi-truck-fast" />
        </span>
      </button>
    </div>
  );
};

export default PromoRaffle;
